<template>
  <div class="ticket-detail">
    <p class="ticket-detail__price">{{ priceText }}</p>
    <div class="ticket-detail__route">
      <div class="ticket-detail__stop">
        <span class="ticket-detail__dot" aria-hidden="true"></span>
        <span class="ticket-detail__name"><span class="app-sr-only">Von </span>{{ fromName }}</span>
      </div>
      <span class="ticket-detail__line" aria-hidden="true"></span>
      <div class="ticket-detail__stop">
        <span class="ticket-detail__dot ticket-detail__dot--end" aria-hidden="true"></span>
        <span class="ticket-detail__name"><span class="app-sr-only">Nach </span>{{ toName }}</span>
      </div>
      <p v-if="tripText" class="ticket-detail__trip">{{ tripText }}</p>
    </div>

    <section class="ticket-detail__section">
      <h3 class="ticket-detail__heading">Ticket</h3>
      <dl class="ticket-detail__list">
        <template v-for="row in ticketRows">
          <dt :key="'t-' + row.label" class="ticket-detail__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="ticket-detail__value">
            <ticket-status-pill v-if="row.status" :status="row.status" :text="row.value" />
            <template v-else>
              <app-icon v-if="row.icon" :name="row.icon" :size="16" class="ticket-detail__icon" />{{ row.value }}
            </template>
          </dd>
        </template>
      </dl>
    </section>

    <section v-if="passengerRows.length" class="ticket-detail__section">
      <h3 class="ticket-detail__heading">Fahrgast</h3>
      <dl class="ticket-detail__list">
        <template v-for="row in passengerRows">
          <dt :key="'t-' + row.label" class="ticket-detail__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="ticket-detail__value">
            <a v-if="row.href" :href="row.href" class="app-link app-link--inline">{{ row.value }}</a>
            <template v-else>{{ row.value }}</template>
          </dd>
        </template>
      </dl>
    </section>

    <section class="ticket-detail__section">
      <h3 class="ticket-detail__heading">Bestellung</h3>
      <dl class="ticket-detail__list">
        <dt class="ticket-detail__label">Bestellung</dt>
        <dd class="ticket-detail__value">
          #{{ ticket.order_id }}
          <a
            v-if="orderUrl"
            :href="orderUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="app-link app-link--inline ticket-detail__shop-link"
          >Im Shop öffnen<app-icon name="external" :size="12" /></a>
        </dd>
        <template v-for="row in orderRows">
          <dt :key="'t-' + row.label" class="ticket-detail__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="ticket-detail__value">{{ row.value }}</dd>
        </template>
      </dl>
    </section>
  </div>
</template>

<script>
// Inhalt des Detailbereichs eines Shop-Tickets (Kundenwunsch 13.10.2026: „unter shop tickets bitte auch zusammenfassen
// sind viel zu viele spalten und mit detailansicht“): Preis und Strecke wie im Verkaufsdetail, darunter „Fahrtdatum ·
// Abfahrt · Fahrt“, dann die übrigen Spalten der früheren Tabelle in Abschnitten – Ticket (Nummer, Status, Check-In,
// Plätze, MwSt., Vertriebskanal, Verkäufer, gekauft, letzte Änderung), Fahrgast (Name, E-Mail und Telefon als Link,
// Flug) und Bestellung (Nummer mit „Im Shop öffnen“ wie bisher der Link der Spalte, Zahlungsanbieter, PayPal-IDs). Zeilen
// ohne Wert entfallen. Nur Anzeige; alle Aktionen (Umbuchen, Stornieren von Ticket oder Bestellung) stehen einheitlich im
// Fuß des Bereichs (Seite). Mobil verkaufte Tickets haben wie bisher keinen Link in den Shop.
import moment from "moment";
import { formatEuro, formatDateTime, formatNumber, LEER } from "@/utils/format";
import { TICKET_STATUS_TEXT, TICKET_TYPE_TEXT, TICKET_TYPE_ICON } from "@/components/shop/shopFilters";
import TicketStatusPill from "@/components/shop/TicketStatusPill";

function present(value) {
  return value !== null && value !== undefined && value !== "";
}

function filled(rows) {
  return rows.filter((row) => present(row.value) && row.value !== LEER);
}

export default {
  name: "ShopTicketDetail",
  components: { TicketStatusPill },
  props: {
    ticket: { type: Object, required: true },
    // „SHOP-5039“
    ticketNo: { type: String, default: "" },
    // Kürzel des Shops für den Link zur Bestellung (customer.shop_url)
    shopUrl: { type: String, default: "" }
  },
  computed: {
    priceText() {
      return present(this.ticket.price) ? formatEuro(Number(this.ticket.price)) : LEER;
    },
    fromName() {
      return present(this.ticket.start_location_name) ? String(this.ticket.start_location_name) : LEER;
    },
    toName() {
      return present(this.ticket.end_location_name) ? String(this.ticket.end_location_name) : LEER;
    },
    // „Dienstag, 13.10.2026 · 06:15 Uhr · Fahrt 111“
    tripText() {
      const t = this.ticket;
      const day = present(t.trip_day) ? moment(t.trip_day).locale("de") : null;
      return [
        day && day.isValid() ? day.format("dddd, DD.MM.YYYY") : null,
        present(t.trip_start) ? String(t.trip_start).substr(0, 5) + " Uhr" : null,
        present(t.trip_id) ? "Fahrt " + t.trip_id : null
      ].filter(Boolean).join(" · ");
    },
    ticketRows() {
      const t = this.ticket;
      return filled([
        { label: "Ticket", value: this.ticketNo },
        { label: "Status", value: TICKET_STATUS_TEXT[t.status] || t.status, status: t.status },
        { label: "Check-In", value: t.checked_in == 1 ? "Eingecheckt" : "Nicht eingecheckt" },
        { label: "Plätze", value: present(t.seats) ? String(t.seats) : null },
        { label: "MwSt.", value: present(t.vat_rate) ? formatNumber(Number(t.vat_rate), 2) + " %" : null },
        { label: "Vertriebskanal", value: TICKET_TYPE_TEXT[t.type] || t.type, icon: TICKET_TYPE_ICON[t.type] || null },
        { label: "Verkäufer", value: t.fullname },
        { label: "Gekauft", value: present(t.time) ? formatDateTime(t.time) : t.date },
        { label: "Letzte Änderung", value: present(t.modification_time) ? formatDateTime(t.modification_time) : null }
      ]);
    },
    passengerRows() {
      const t = this.ticket;
      const name = [t.first_name, t.last_name].filter(present).join(" ");
      return filled([
        { label: "Name", value: name },
        { label: "E-Mail", value: t.mail, href: present(t.mail) ? "mailto:" + t.mail : null },
        { label: "Telefon", value: t.phone, href: present(t.phone) ? "tel:" + String(t.phone).replace(/\s+/g, "") : null },
        { label: "Flug", value: t.flight_number }
      ]);
    },
    orderUrl() {
      const t = this.ticket;
      if (t.type == "MOBILE" || !this.shopUrl || !present(t.order_uuid)) {
        return null;
      }
      return `https://shop.bconn.de/${this.shopUrl}/summary?order_uuid=${t.order_uuid}&locale=de`;
    },
    orderRows() {
      const t = this.ticket;
      const provider = t.paypal_order_id || t.paypal_capture_id ? "PayPal" : t.stripe_payment_intent ? "Stripe" : null;
      return filled([
        { label: "Zahlung", value: provider },
        { label: "PayPal Order-ID", value: t.paypal_order_id },
        { label: "PayPal Capture-ID", value: t.paypal_capture_id }
      ]);
    }
  }
};
</script>

<style scoped>
.ticket-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
p.ticket-detail__price {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -.01em;
  color: var(--ink);
  white-space: nowrap;
}
/* Strecke wie im Verkaufsdetail: Start hohl, Ziel gefüllt, gepunktete Führungslinie */
.ticket-detail__route {
  display: flex;
  flex-direction: column;
}
.ticket-detail__stop {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.ticket-detail__dot {
  width: 10px;
  height: 10px;
  flex: none;
  box-sizing: border-box;
  border: 2px solid var(--d-foc);
  border-radius: 50%;
}
.ticket-detail__dot--end {
  background: var(--d-foc);
}
.ticket-detail__line {
  width: 0;
  height: 16px;
  margin-left: 4px;
  border-left: 2px dotted var(--d-prv);
}
.ticket-detail__name {
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  overflow-wrap: anywhere;
}
p.ticket-detail__trip {
  margin: 8px 0 0;
  padding-left: 22px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
/* Abschnitte wie im Verkaufsdetail */
.ticket-detail__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
h3.ticket-detail__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.ticket-detail__list {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: 8px 12px;
  margin: 0;
}
.ticket-detail__label {
  font-size: 12px;
  line-height: 20px;
  color: var(--ink-3);
}
.ticket-detail__value {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
/* Vertriebskanal mit Icon wie in der Tabelle */
.ticket-detail__icon {
  margin-right: 6px;
  vertical-align: -3px;
  color: var(--ink-2);
}
.ticket-detail__shop-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  font-size: 13px;
  line-height: 20px;
}
</style>
